/**
 * Utilidades de ubicacion con Google Maps, compartidas por el panel y la web.
 *
 * Objetivo: mostrar la ubicacion EXACTA de un negocio sin habilitar ningun
 * servicio facturable. Este modulo solo analiza y valida enlaces que la persona
 * pega a mano, y construye URLs para:
 *
 *   * Maps Embed API (`/maps/embed/v1/...`), gratis e ilimitada, cuando existe
 *     una clave publica restringida a esa API.
 *   * El embed clasico sin clave (`maps.google.com/maps?...&output=embed`)
 *     como respaldo, que tampoco tiene costo.
 *
 * Nunca se llama a Geocoding API ni a Places, nunca se sigue una redireccion
 * para "adivinar" coordenadas y nunca se inventa una ubicacion: si el enlace no
 * trae coordenadas legibles (por ejemplo un enlace corto) se conserva tal cual
 * para abrirlo en Google Maps, pero no se asume que se pueda incrustar.
 */

export interface MapCoordinates {
  latitude: number;
  longitude: number;
}

export function isValidLatitude(value: number): boolean {
  return Number.isFinite(value) && value >= -90 && value <= 90;
}

export function isValidLongitude(value: number): boolean {
  return Number.isFinite(value) && value >= -180 && value <= 180;
}

/** Lee un par "lat,lng" (con o sin etiqueta posterior) y lo valida. */
export function parseCoordinatePair(value: string | null | undefined): MapCoordinates | null {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\b/);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;
  return { latitude, longitude };
}

export type GoogleMapsLinkKind = "coords" | "place" | "short" | "query" | "invalid";

export interface GoogleMapsLinkInfo {
  kind: GoogleMapsLinkKind;
  /** Enlace original validado (null si `kind === "invalid"`). */
  url: string | null;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
  /** Texto de busqueda del enlace, nunca coordenadas. */
  query: string | null;
}

const INVALID_LINK: GoogleMapsLinkInfo = {
  kind: "invalid",
  url: null,
  latitude: null,
  longitude: null,
  placeId: null,
  query: null,
};

const MAPS_HOSTS = new Set([
  "google.com",
  "www.google.com",
  "maps.google.com",
  "google.com.pe",
  "www.google.com.pe",
]);

function matchPair(text: string, regex: RegExp): MapCoordinates | null {
  const match = text.match(regex);
  if (!match) return null;
  const latitude = Number(match[1]);
  const longitude = Number(match[2]);
  if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;
  return { latitude, longitude };
}

/**
 * Analiza un enlace de Google Maps (o un par "lat,lng" pegado a mano).
 *
 * Formatos reconocidos con coordenadas legibles:
 *   - `https://www.google.com/maps/@-12.04,-77.02,15z`
 *   - `.../maps/place/Nombre/@-12.04,-77.02,17z/data=!3d-12.04!4d-77.02`
 *   - `.../maps?q=-12.04,-77.02`
 *   - `.../maps?ll=-12.04,-77.02` / `?center=` / `?query=`
 *
 * Enlaces cortos (`maps.app.goo.gl`, `goo.gl/maps`) se reconocen pero NO se
 * siguen ni se asume que tengan coordenadas.
 */
export function parseGoogleMapsLink(input: string | null | undefined): GoogleMapsLinkInfo {
  if (typeof input !== "string") return INVALID_LINK;
  const trimmed = input.trim();
  if (!trimmed) return INVALID_LINK;

  const barePair = parseCoordinatePair(trimmed);
  if (barePair) {
    return {
      kind: "coords",
      url: null,
      latitude: barePair.latitude,
      longitude: barePair.longitude,
      placeId: null,
      query: null,
    };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return INVALID_LINK;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return INVALID_LINK;

  const host = url.hostname.toLowerCase();
  const path = url.pathname.toLowerCase();

  if (host === "maps.app.goo.gl" || (host === "goo.gl" && path.startsWith("/maps"))) {
    return { kind: "short", url: trimmed, latitude: null, longitude: null, placeId: null, query: null };
  }

  if (!MAPS_HOSTS.has(host) || (host !== "maps.google.com" && !path.startsWith("/maps"))) {
    return INVALID_LINK;
  }

  // `decodeURIComponent` lanza `URIError` con porcentajes malformados pegados a
  // mano (p. ej. ".../%"), lo que rompería el render de los formularios. En ese
  // caso se sigue con el texto original: solo afecta a la busqueda de `!3d/!4d`,
  // que igualmente se puede leer del texto crudo.
  let decoded = trimmed;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    // Entrada con porcentajes invalidos: no es un error fatal del analisis.
  }

  // El segmento `!3d..!4d..` es la ubicacion exacta del lugar; el prefijo
  // `@lat,lng` suele ser solo el centro del viewport, asi que se prefiere el
  // primero cuando existe.
  let coords = matchPair(decoded, /!3d(-?\d{1,3}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/);
  if (!coords) coords = matchPair(url.pathname, /@(-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/);
  if (!coords) {
    for (const key of ["q", "query", "ll", "center", "daddr", "destination"]) {
      const pair = parseCoordinatePair(url.searchParams.get(key));
      if (pair) {
        coords = pair;
        break;
      }
    }
  }

  const placeId = url.searchParams.get("query_place_id") ?? url.searchParams.get("place_id") ?? null;

  const queryParam = url.searchParams.get("q") ?? url.searchParams.get("query");
  const query = queryParam && !parseCoordinatePair(queryParam) ? queryParam : null;

  if (coords) {
    return {
      kind: "coords",
      url: trimmed,
      latitude: coords.latitude,
      longitude: coords.longitude,
      placeId,
      query: null,
    };
  }
  if (placeId) {
    return { kind: "place", url: trimmed, latitude: null, longitude: null, placeId, query };
  }
  return { kind: "query", url: trimmed, latitude: null, longitude: null, placeId: null, query };
}

/** Devuelve el enlace normalizado si es un enlace de Google Maps reconocido. */
export function normalizeGoogleMapsUrl(input: string | null | undefined): string | null {
  return parseGoogleMapsLink(input).url;
}

/** Clasificacion de lo que el usuario escribio en el campo de ubicacion. */
export interface LocationInputResolution {
  /**
   * URL de Google Maps valida (admite enlaces cortos, que se conservan para
   * abrir en Google Maps aunque no se puedan incrustar). null si la entrada no
   * es una URL, incluido el caso de un par de coordenadas suelto.
   */
  googleMapsUrl: string | null;
  /**
   * Coordenadas interpretadas de un par "lat,lng" suelto. Se guardan en las
   * columnas numericas, nunca como URL.
   */
  coordinates: MapCoordinates | null;
  /** true solo si la entrada no es ni una URL valida ni un par de coordenadas. */
  invalid: boolean;
}

/**
 * Interpreta una unica entrada de ubicacion con un contrato definido:
 *   * Vacío       -> sin ubicacion.
 *   * "lat,lng"   -> coordenadas (van a `latitude`/`longitude`, jamas a la URL).
 *   * URL de Maps -> `googleMapsUrl` (enlaces cortos incluidos).
 *   * Cualquier otro texto o dominio no autorizado -> `invalid`.
 */
export function resolveLocationInput(input: string | null | undefined): LocationInputResolution {
  const trimmed = typeof input === "string" ? input.trim() : "";
  if (!trimmed) {
    return { googleMapsUrl: null, coordinates: null, invalid: false };
  }

  const coordinates = parseCoordinatePair(trimmed);
  if (coordinates) {
    return { googleMapsUrl: null, coordinates, invalid: false };
  }

  const link = parseGoogleMapsLink(trimmed);
  if (link.url) {
    return { googleMapsUrl: trimmed, coordinates: null, invalid: false };
  }

  return { googleMapsUrl: null, coordinates: null, invalid: true };
}

export interface MapEmbedOptions {
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string | null;
  query?: string | null;
}

/**
 * Construye el `src` del iframe. Con clave usa Maps Embed API (gratis); sin
 * clave cae al embed clasico, que tambien es gratuito. Devuelve null cuando no
 * hay suficiente informacion o cuando haria falta una clave (place_id).
 */
export function buildGoogleMapsEmbedUrl(options: MapEmbedOptions, apiKey?: string): string | null {
  const { latitude, longitude, placeId, query } = options;
  const key = apiKey?.trim();

  if (typeof latitude === "number" && typeof longitude === "number") {
    if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;
    const center = `${latitude},${longitude}`;
    if (key) {
      return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(center)}&zoom=16`;
    }
    return `https://maps.google.com/maps?q=${encodeURIComponent(center)}&z=16&output=embed`;
  }

  if (placeId) {
    if (!key) return null;
    return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=place_id:${encodeURIComponent(placeId)}`;
  }

  if (query) {
    if (key) {
      return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(query)}`;
    }
    return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
  }

  return null;
}

/** Enlace "Como llegar". Prioriza coordenadas, luego place_id, luego texto. */
export function buildGoogleMapsDirectionsUrl(options: MapEmbedOptions): string | null {
  const { latitude, longitude, placeId, query } = options;

  if (typeof latitude === "number" && typeof longitude === "number") {
    if (!isValidLatitude(latitude) || !isValidLongitude(longitude)) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${latitude},${longitude}`)}`;
  }
  if (placeId) {
    return `https://www.google.com/maps/dir/?api=1&destination=place_id:${encodeURIComponent(placeId)}`;
  }
  if (query) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }
  return null;
}

/** Datos de ubicacion de un negocio (reales o de la solicitud). */
export interface StoreLocation {
  latitude?: number | null;
  longitude?: number | null;
  google_maps_url?: string | null;
  address?: string | null;
  district?: string | null;
  province?: string | null;
  department?: string | null;
  city?: string | null;
}

export interface MapView {
  /** `src` del iframe, o null si no se puede incrustar sin asumir datos. */
  embedUrl: string | null;
  /** Enlace "Como llegar" (destino exacto o el enlace compartido guardado). */
  directionsUrl: string | null;
  /** true solo con coordenadas confirmadas: la ubicacion es exacta. */
  exact: boolean;
  /** true si el mapa incrustado es aproximado (direccion o texto, no coords). */
  approximate: boolean;
  /** Texto de la ubicacion para mostrar junto al mapa. */
  label: string;
}

/**
 * Resuelve que mapa mostrar para un negocio:
 *   1. Coordenadas guardadas o extraidas del enlace -> mapa exacto.
 *   2. Place ID del enlace -> mapa del lugar (requiere clave).
 *   3. Texto del enlace o la direccion -> mapa aproximado (sin clave).
 *   4. Sin nada incrustable -> solo enlace "Como llegar" con la direccion.
 */
export function resolveMapView(location: StoreLocation, apiKey?: string): MapView {
  const addressLine = [location.address, location.district, location.province, location.department]
    .filter((value): value is string => Boolean(value && value.trim()))
    .join(", ");
  const fallbackLabel = location.city?.trim() || "";

  const storedCoords =
    typeof location.latitude === "number" &&
    typeof location.longitude === "number" &&
    isValidLatitude(location.latitude) &&
    isValidLongitude(location.longitude)
      ? { latitude: location.latitude, longitude: location.longitude }
      : null;

  const parsed = parseGoogleMapsLink(location.google_maps_url);
  const linkCoords =
    parsed.kind === "coords" && parsed.latitude !== null && parsed.longitude !== null
      ? { latitude: parsed.latitude, longitude: parsed.longitude }
      : null;

  const coords = storedCoords ?? linkCoords;

  if (coords) {
    return {
      embedUrl: buildGoogleMapsEmbedUrl(coords, apiKey),
      directionsUrl: buildGoogleMapsDirectionsUrl(coords),
      exact: true,
      approximate: false,
      label: addressLine || `${coords.latitude}, ${coords.longitude}`,
    };
  }

  let embedUrl: string | null = null;
  if (parsed.placeId) embedUrl = buildGoogleMapsEmbedUrl({ placeId: parsed.placeId }, apiKey);
  if (!embedUrl && parsed.query) embedUrl = buildGoogleMapsEmbedUrl({ query: parsed.query }, apiKey);
  if (!embedUrl && addressLine) {
    embedUrl = buildGoogleMapsEmbedUrl({ query: `${addressLine}, Perú` }, apiKey);
  }

  const directionsUrl =
    parsed.url ?? (addressLine ? buildGoogleMapsDirectionsUrl({ query: `${addressLine}, Perú` }) : null);

  return {
    embedUrl,
    directionsUrl,
    exact: false,
    approximate: Boolean(embedUrl) && !parsed.placeId,
    label: addressLine || fallbackLabel,
  };
}
